<h2 mat-dialog-title>{{ dialogTitle }}</h2>

<mat-dialog-content class="mat-typography">
    <p>Complete the details below to generate a shareable application link for the client.</p>

    <form
        *ngIf="applicationLinkForm"
        [formGroup]="applicationLinkForm"
        (ngSubmit)="onSubmit()"
        novalidate
        #ngForm="ngForm"
    >
        <div fxLayout="column" fxLayoutGap="15px">
            <!-- Client Name -->
            <fieldset formGroupName="clientName" class="fieldset-group">
                <legend>Client Name</legend>
                <div fxLayout="row wrap" fxLayoutGap="15px grid">
                    <mat-form-field appearance="outline" fxFlex="100%" fxFlex.gt-xs="calc(33.33% - 10px)">
                        <mat-label>First Name</mat-label>
                        <input matInput formControlName="firstName" autocomplete="given-name" required />
                        <mat-error
                            *ngIf="
                                clientNameForm?.get('firstName')?.touched &&
                                clientNameForm?.get('firstName')?.hasError('required')
                            "
                        >
                            First name is required.
                        </mat-error>
                        <mat-error
                            *ngIf="
                                clientNameForm?.get('firstName')?.touched &&
                                clientNameForm?.get('firstName')?.hasError('maxlength')
                            "
                        >
                            First name cannot exceed 100 characters.
                        </mat-error>
                    </mat-form-field>

                    <mat-form-field appearance="outline" fxFlex="100%" fxFlex.gt-xs="calc(33.33% - 10px)">
                        <mat-label>Middle Name (Optional)</mat-label>
                        <input matInput formControlName="middleName" autocomplete="additional-name" />
                        <mat-error
                            *ngIf="
                                clientNameForm?.get('middleName')?.touched &&
                                clientNameForm?.get('middleName')?.hasError('maxlength')
                            "
                        >
                            Middle name cannot exceed 100 characters.
                        </mat-error>
                    </mat-form-field>

                    <mat-form-field appearance="outline" fxFlex="100%" fxFlex.gt-xs="calc(33.33% - 10px)">
                        <mat-label>Last Name</mat-label>
                        <input matInput formControlName="lastName" autocomplete="family-name" required />
                        <mat-error
                            *ngIf="
                                clientNameForm?.get('lastName')?.touched &&
                                clientNameForm?.get('lastName')?.hasError('required')
                            "
                        >
                            Last name is required.
                        </mat-error>
                        <mat-error
                            *ngIf="
                                clientNameForm?.get('lastName')?.touched &&
                                clientNameForm?.get('lastName')?.hasError('maxlength')
                            "
                        >
                            Last name cannot exceed 100 characters.
                        </mat-error>
                    </mat-form-field>
                </div>
            </fieldset>

            <!-- Client Details -->
            <fieldset class="fieldset-group">
                <legend>Client Details</legend>
                <div fxLayout="row wrap" fxLayoutGap="15px grid">
                    <mat-form-field appearance="outline" fxFlex="100%" fxFlex.gt-xs="calc(50% - 7.5px)">
                        <mat-label>Client Email</mat-label>
                        <input
                            matInput
                            type="email"
                            formControlName="clientEmail"
                            placeholder="e.g., client@example.com"
                            autocomplete="email"
                            required
                        />
                        <mat-error
                            *ngIf="
                                formControls?.clientEmail?.touched && formControls?.clientEmail?.hasError('required')
                            "
                        >
                            Client email is required.
                        </mat-error>
                        <mat-error
                            *ngIf="formControls?.clientEmail?.touched && formControls?.clientEmail?.hasError('email')"
                        >
                            Please enter a valid email address.
                        </mat-error>
                        <mat-error
                            *ngIf="
                                formControls?.clientEmail?.touched && formControls?.clientEmail?.hasError('maxlength')
                            "
                        >
                            Email address cannot exceed 255 characters.
                        </mat-error>
                    </mat-form-field>

                    <mat-form-field appearance="outline" fxFlex="100%" fxFlex.gt-xs="calc(50% - 7.5px)">
                        <mat-label>Client Phone Number</mat-label>
                        <input
                            matInput
                            type="tel"
                            formControlName="clientPhoneNumber"
                            placeholder="e.g., 0412 345 678"
                            autocomplete="tel"
                            required
                        />
                        <mat-error
                            *ngIf="
                                formControls?.clientPhoneNumber?.touched &&
                                formControls?.clientPhoneNumber?.hasError('required')
                            "
                        >
                            Client phone number is required.
                        </mat-error>
                        <mat-error
                            *ngIf="
                                formControls?.clientPhoneNumber?.touched &&
                                formControls?.clientPhoneNumber?.hasError('pattern')
                            "
                        >
                            Please enter a valid Australian phone number.
                        </mat-error>
                        <mat-error
                            *ngIf="
                                formControls?.clientPhoneNumber?.touched &&
                                formControls?.clientPhoneNumber?.hasError('maxlength')
                            "
                        >
                            Phone number cannot exceed 20 characters.
                        </mat-error>
                    </mat-form-field>
                </div>
            </fieldset>

            <!-- Client Residential Address -->
            <fieldset class="fieldset-group">
                <legend>Client Residential Address</legend>
                <bp-address
                    formGroupName="clientResidentialAddress"
                    [states]="addressStatesForBpAddress"
                    data-test-id="client-residential-address"
                ></bp-address>

                <mat-error
                    *ngIf="
                        clientAddressForm?.get('fullAddress')?.hasError('selectedAddressRequired') &&
                        clientAddressForm?.get('fullAddress')?.touched
                    "
                    data-test-id="fullAddress-selected-error"
                >
                    Please select a valid address from the suggestions after searching, or complete manual entry.
                </mat-error>
                <mat-error
                    *ngIf="
                        clientAddressForm?.get('fullAddress')?.hasError('_internalError') &&
                        clientAddressForm?.get('fullAddress')?.touched
                    "
                    data-test-id="fullAddress-internal-error"
                >
                    There was an issue searching for the address. Please try again or enter manually.
                </mat-error>
                <mat-error
                    *ngIf="
                        clientAddressForm?.hasError('addressIncomplete') &&
                        (clientAddressForm?.touched || clientAddressForm?.dirty)
                    "
                    data-test-id="address-incomplete-error"
                >
                    Please either search and select an address, or complete all required manual address fields (Street
                    No, Street, Suburb, Postcode, State).
                </mat-error>
                <mat-error
                    *ngIf="
                        clientAddressForm?.hasError('addressSelectionOrManualRequired') &&
                        (clientAddressForm?.touched || clientAddressForm?.dirty)
                    "
                    data-test-id="address-selection-or-manual-error"
                >
                    Please select an address from the search results or complete the manual address fields.
                </mat-error>
                <mat-error
                    *ngIf="
                        clientAddressForm?.get('state')?.hasError('heslpNswOnly') &&
                        clientAddressForm?.get('state')?.touched
                    "
                    data-test-id="heslp-nsw-only-error"
                >
                    HESLP is only available for NSW addresses.
                </mat-error>
            </fieldset>

            <!-- System Information -->
            <fieldset class="fieldset-group">
                <legend>System Information</legend>

                <!-- System Cost -->
                <mat-form-field appearance="outline" fxFlex="100%">
                    <mat-label>System Cost ($)</mat-label>
                        <input
                            matInput
                            type="text"
                            formControlName="systemCost"
                            bpCurrencyFormat
                            placeholder="e.g., 10,000"
                            autocomplete="off"
                            required
                           [matTooltip]="dialogInputData.isSystemCostDisabled ? 'This value is automatically populated from the quote and cannot be edited.' : ''"
                            matTooltipPosition="above"
                        />
                    <mat-error
                        *ngIf="formControls?.systemCost?.touched && formControls?.systemCost?.hasError('required')"
                    >
                        System cost is required.
                    </mat-error>
                    <mat-error *ngIf="formControls?.systemCost?.touched && formControls?.systemCost?.hasError('min')">
                        System cost must be greater than $1000.
                    </mat-error>
                    <mat-error *ngIf="formControls?.systemCost?.touched && formControls?.systemCost?.hasError('max')">
                        Maximum loan amount for HESLP is $15,000.
                    </mat-error>
                    <mat-error
                        *ngIf="formControls?.systemCost?.touched && formControls?.systemCost?.hasError('pattern')"
                    >
                        Please enter a valid whole dollar amount.
                    </mat-error>
                </mat-form-field>

                <!-- System Type(s) -->
                <bp-renewable-system-types
                    formControlName="systemType"
                    [productTag]="dialogInputData.productTag"
                    data-test-id="interest-free-system-types"
                    required
                ></bp-renewable-system-types>
                <mat-error *ngIf="formControls?.systemType?.touched && formControls?.systemType?.hasError('required')">
                    At least one system type is required.
                </mat-error>
                <mat-error
                    *ngIf="formControls?.systemType?.touched && formControls?.systemType?.hasError('heslpSolarOnly')"
                    data-test-id="heslp-solar-only-error"
                >
                    HESLP requires battery or heat pump. Solar-only is not eligible.
                </mat-error>

                <!-- Conditional System Size Fields -->
                <div *ngIf="shouldShowSolarPanelSize()" class="system-size-field">
                    <mat-form-field appearance="outline" fxFlex="100%">
                        <mat-label>Solar Panel Size (kW)</mat-label>
                        <input
                            matInput
                            type="number"
                            formControlName="solarPanelSize"
                            placeholder="e.g., 6.6"
                            autocomplete="off"
                            step="0.1"
                            min="0"
                            required
                            (wheel)="$event.preventDefault()"
                        />
                        <mat-error
                            *ngIf="
                                formControls?.solarPanelSize?.touched &&
                                formControls?.solarPanelSize?.hasError('required')
                            "
                        >
                            Solar panel size is required when solar panels are selected.
                        </mat-error>
                        <mat-error
                            *ngIf="
                                formControls?.solarPanelSize?.touched && formControls?.solarPanelSize?.hasError('min')
                            "
                        >
                            Solar panel size must be greater than 0.
                        </mat-error>
                        <mat-error
                            *ngIf="
                                formControls?.solarPanelSize?.touched &&
                                formControls?.solarPanelSize?.hasError('pattern')
                            "
                        >
                            Please enter a valid solar panel size (e.g., 6.6).
                        </mat-error>
                    </mat-form-field>
                </div>

                <div *ngIf="shouldShowBatterySize()" class="system-size-field">
                    <mat-form-field appearance="outline" fxFlex="100%">
                        <mat-label>Battery Size (kWh)</mat-label>
                        <input
                            matInput
                            type="number"
                            formControlName="batterySize"
                            placeholder="e.g., 13.5"
                            autocomplete="off"
                            step="0.1"
                            min="0"
                            required
                            (wheel)="$event.preventDefault()"
                        />
                        <mat-error
                            *ngIf="
                                formControls?.batterySize?.touched && formControls?.batterySize?.hasError('required')
                            "
                        >
                            Battery size is required when battery is selected.
                        </mat-error>
                        <mat-error
                            *ngIf="formControls?.batterySize?.touched && formControls?.batterySize?.hasError('min')"
                        >
                            Battery size must be greater than 0.
                        </mat-error>
                        <mat-error
                            *ngIf="formControls?.batterySize?.touched && formControls?.batterySize?.hasError('pattern')"
                        >
                            Please enter a valid battery size (e.g., 13.5).
                        </mat-error>
                    </mat-form-field>
                </div>
            </fieldset>

            <!-- Electricity Bill Savings -->
            <fieldset formGroupName="electricityBillSavings" class="fieldset-group electricity-savings">
                <legend>Estimated Electricity Bill Savings</legend>
                <div fxLayout="row wrap" fxLayoutGap="15px grid">
                    <mat-form-field appearance="outline" fxFlex="100%" fxFlex.gt-xs="calc(50% - 7.5px)">
                        <mat-label>Amount ($)</mat-label>
                        <input
                            matInput
                            type="text"
                            bpCurrencyFormat
                            [allowDecimals]="true"
                            [maxDecimalPlaces]="2"
                            formControlName="amount"
                            placeholder="e.g., 250"
                            autocomplete="off"
                            required
                        />
                        <mat-error
                            *ngIf="
                                electricityBillSavingsForm?.get('amount')?.touched &&
                                electricityBillSavingsForm?.get('amount')?.hasError('required')
                            "
                        >
                            Amount is required.
                        </mat-error>
                        <mat-error
                            *ngIf="
                                electricityBillSavingsForm?.get('amount')?.touched &&
                                electricityBillSavingsForm?.get('amount')?.hasError('min')
                            "
                        >
                            Amount must be zero or greater.
                        </mat-error>
                        <mat-error
                            *ngIf="
                                electricityBillSavingsForm?.get('amount')?.touched &&
                                electricityBillSavingsForm?.get('amount')?.hasError('pattern')
                            "
                        >
                            Please enter a valid amount (e.g., 250 or 250.50).
                        </mat-error>
                    </mat-form-field>

                    <mat-form-field appearance="outline" fxFlex="100%" fxFlex.gt-xs="calc(50% - 7.5px)">
                        <mat-label>Frequency</mat-label>
                        <mat-select formControlName="frequency" required>
                            <mat-option *ngFor="let freq of sortedSavingFrequencies" [value]="freq.value">
                                {{ freq.value }}
                            </mat-option>
                        </mat-select>
                        <mat-error
                            *ngIf="
                                electricityBillSavingsForm?.get('frequency')?.touched &&
                                electricityBillSavingsForm?.get('frequency')?.hasError('required')
                            "
                        >
                            Frequency is required.
                        </mat-error>
                    </mat-form-field>
                </div>

                <div class="confirmation">
                    <label id="pricing-explanation-label" class="mat-body-1 confirmation-text">
                        I confirm that the estimated electricity savings have been explained to the customer and are
                        based on reasonable assumptions and, where available, from reputable sources.
                    </label>

                    <mat-button-toggle-group
                        formControlName="pricingExplanationConfirmation"
                        aria-labelledby="pricing-explanation-label"
                        class="savings-toggle-group"
                    >
                        <mat-button-toggle [value]="true" data-test-id="pricing-explanation-yes-toggle">
                            Yes
                        </mat-button-toggle>
                        <mat-button-toggle [value]="false" data-test-id="pricing-explanation-no-toggle">
                            No
                        </mat-button-toggle>
                    </mat-button-toggle-group>

                    <mat-error
                        *ngIf="
                            electricityBillSavingsForm?.get('pricingExplanationConfirmation')?.touched &&
                            electricityBillSavingsForm?.get('pricingExplanationConfirmation')?.hasError('required')
                        "
                        data-test-id="pricing-explanation-error"
                    >
                        Confirmation that the savings have been explained is required (select 'Yes').
                    </mat-error>
                </div>
            </fieldset>

            <!-- Income Threshold Confirmation (for WABS and HESLP) -->
            <fieldset class="fieldset-group" *ngIf="applicationLinkForm?.get('totalIncomeLessThanThresholdConfirmed')">
                <legend>Income Eligibility</legend>
                <div class="confirmation">
                    <label id="totalIncomeLessThanThresholdConfirmed-label" class="mat-body-1 confirmation-text">
                        I confirm my customers household income is less than $210,000
                    </label>

                    <mat-button-toggle-group
                        formControlName="totalIncomeLessThanThresholdConfirmed"
                        aria-labelledby="totalIncomeLessThanThresholdConfirmed-label"
                    >
                        <mat-button-toggle
                            [value]="true"
                            data-test-id="totalIncomeLessThanThresholdConfirmed-yes-toggle"
                        >
                            Yes
                        </mat-button-toggle>
                        <mat-button-toggle
                            [value]="false"
                            data-test-id="totalIncomeLessThanThresholdConfirmed-no-toggle"
                        >
                            No
                        </mat-button-toggle>
                    </mat-button-toggle-group>

                    <mat-error
                        *ngIf="
                            (ngForm?.submitted ||
                                applicationLinkForm?.get('totalIncomeLessThanThresholdConfirmed')?.touched) &&
                            applicationLinkForm?.get('totalIncomeLessThanThresholdConfirmed')?.hasError('required')
                        "
                        data-test-id="totalIncomeLessThanThresholdConfirmed-error"
                    >
                        Confirmation that the household income is less than $210,000 (select 'Yes').
                    </mat-error>
                </div>
            </fieldset>
        </div>
    </form>
</mat-dialog-content>

<mat-dialog-actions align="end">
    <button mat-stroked-button type="button" (click)="onCancel()" [disabled]="isLoading" data-test-id="cancel-button">
        Cancel
    </button>
    <button
        mat-raised-button
        color="primary"
        type="submit"
        (click)="onSubmit()"
        [disabled]="isLoading || (applicationLinkForm && applicationLinkForm.invalid)"
        data-test-id="submit-button"
    >
        <ng-container *ngIf="!isLoading; else loadingContent">
            Send Link
        </ng-container>
        <ng-template #loadingContent>
            <span style="display: inline-flex; align-items: center;">
                <mat-spinner [diameter]="20" [strokeWidth]="2"></mat-spinner>
                <span style="margin-left: 8px;">Sending...</span>
            </span>
        </ng-template>
    </button>
</mat-dialog-actions>
